import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { FaEye, FaFilter, FaDownload, FaSearch, FaFilePdf, FaFileAlt, FaComment, FaPaperPlane, FaEdit } from 'react-icons/fa';
import { incidentReportsAPI } from '../../utils/api';
import { API_BASE_URL } from '../../config';
import notificationService from '../../services/notificationService';
import './IncidentReports.css';

const IncidentReports = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState('');

  const [filters, setFilters] = useState({
    severity: '',
    status: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    submitted: 0,
    underInvestigation: 0,
    resolved: 0,
    critical: 0
  });

  useEffect(() => {
    fetchReports();
    // Stats will be calculated from the fetched reports
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, reports]);

  const fetchStats = async () => {
    try {
      const response = await incidentReportsAPI.getStats();
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await incidentReportsAPI.getAllReports();
      const reportsData = response.data.data || response.data || [];
      console.log('=== INCIDENT REPORTS FETCH DEBUG ===');
      console.log('Fetched reports:', reportsData);
      if (reportsData.length > 0) {
        console.log('First report structure:', reportsData[0]);
        console.log('First report region:', reportsData[0].region);
        console.log('First report branch:', reportsData[0].branch);
        console.log('First report siteId:', reportsData[0].siteId);
        console.log('First report Site object:', reportsData[0].Site);
      }
      console.log('=== END DEBUG ===');
      setReports(reportsData);
      // Always recalculate stats to reflect current state
      calculateStats(reportsData);
    } catch (error) {
      console.error('Error fetching incident reports:', error);
      setError('Failed to load incident reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (reportsData) => {
    const newStats = {
      total: reportsData.length,
      submitted: reportsData.filter(r => r.status === 'submitted' || r.status === 'pending').length,
      underInvestigation: reportsData.filter(r => r.status === 'under_investigation' || r.status === 'reviewed').length,
      resolved: reportsData.filter(r => r.status === 'resolved').length,
      critical: reportsData.filter(r => r.severity === 'critical').length
    };
    console.log('Calculated stats:', newStats);
    setStats(newStats);
  };

  const applyFilters = () => {
    let filtered = [...reports];

    // Severity filter
    if (filters.severity) {
      filtered = filtered.filter(r => r.severity === filters.severity);
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(r => r.status === filters.status);
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(r =>
        r.title?.toLowerCase().includes(searchLower) ||
        r.description?.toLowerCase().includes(searchLower) ||
        r.location?.toLowerCase().includes(searchLower) ||
        r.reportedBy?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Date range filter
    if (filters.startDate) {
      filtered = filtered.filter(r => new Date(r.createdAt) >= new Date(filters.startDate));
    }
    if (filters.endDate) {
      filtered = filtered.filter(r => new Date(r.createdAt) <= new Date(filters.endDate));
    }

    setFilteredReports(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      severity: '',
      status: '',
      search: '',
      startDate: '',
      endDate: ''
    });
  };

  const viewReport = async (reportId) => {
    try {
      const response = await incidentReportsAPI.getReportById(reportId);
      // Handle nested response structure: response.data.data or response.data
      const reportData = response.data.data || response.data;

      console.log('=== INCIDENT REPORT DEBUG ===');
      console.log('Full Response:', response.data);
      console.log('Report Data:', reportData);
      console.log('All Report Keys:', Object.keys(reportData));
      console.log('--- Location Fields Check ---');
      console.log('location:', reportData.location);
      console.log('region:', reportData.region);
      console.log('branch:', reportData.branch);
      console.log('siteId:', reportData.siteId);
      console.log('Site object:', reportData.Site);
      console.log('--- Photo Fields Check ---');
      console.log('photos:', reportData.photos);
      console.log('photoPath:', reportData.photoPath);
      console.log('photoPaths:', reportData.photoPaths);
      console.log('photoUrls:', reportData.photoUrls);
      console.log('photoCount:', reportData.photoCount);
      console.log('attachments:', reportData.attachments);
      console.log('images:', reportData.images);
      console.log('--- Reporter Fields Check ---');
      console.log('reportedBy:', reportData.reportedBy);
      console.log('user:', reportData.user);
      console.log('User:', reportData.User);
      console.log('reporter:', reportData.reporter);
      console.log('personnel:', reportData.personnel);
      console.log('Personnel:', reportData.Personnel);
      console.log('=== END DEBUG ===');

      setSelectedReport(reportData);
      setComments([]);
      setCommentText('');
      setCommentError('');
      setShowModal(true);

      // Fetch comments in background
      fetchComments(reportId);
    } catch (error) {
      console.error('Error fetching report details:', error);
      setError('Failed to load report details.');
    }
  };

  const fetchComments = async (reportId) => {
    try {
      const response = await incidentReportsAPI.getComments(reportId);
      const data = response.data?.data || response.data || [];
      setComments(Array.isArray(data) ? data : []);
    } catch (err) {
      // Non-critical — silently ignore if endpoint not yet available
      setComments([]);
    }
  };

  const submitComment = async () => {
    if (!commentText.trim() || !selectedReport) return;
    setCommentLoading(true);
    setCommentError('');
    try {
      const response = await incidentReportsAPI.addComment(selectedReport.id, { body: commentText.trim() });
      const newComment = response.data?.data || response.data;
      setComments(prev => [...prev, newComment]);
      setCommentText('');
    } catch (err) {
      setCommentError(err.response?.data?.message || 'Failed to post comment. Please try again.');
    } finally {
      setCommentLoading(false);
    }
  };

  const updateReportStatus = async (reportId, status, adminNotes = '') => {
    try {
      console.log('Updating report status:', { reportId, status, adminNotes });
      const response = await incidentReportsAPI.updateStatus(reportId, { status, adminNotes });
      console.log('Update response:', response);

      // If we sent 'dismissed', backend maps it to 'resolved'
      const actualStatus = status === 'dismissed' ? 'resolved' : status;

      // Update the selected report immediately for UI feedback
      if (selectedReport && selectedReport.id === reportId) {
        setSelectedReport({ ...selectedReport, status: actualStatus });
      }

      // Trigger notification to other admins and sub-admins
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const personnelName = selectedReport?.user?.name || 
                            selectedReport?.User?.name || 
                            selectedReport?.submittedBy || 
                            'Personnel';
      const siteName = selectedReport?.Site?.name || 
                       selectedReport?.siteName || 
                       'Unknown Site';

      notificationService.triggerIncidentAction('incident-status-updated', {
        adminName: currentUser?.name || currentUser?.username || 'Admin',
        adminId: currentUser?.id || currentUser?.userId,
        incidentTitle: selectedReport?.title || 'Incident Report',
        newStatus: actualStatus,
        siteName: siteName,
        personnelName: personnelName,
        severity: selectedReport?.severity,
        branch: selectedReport?.branch,
        region: selectedReport?.region
      });

      // Fetch fresh data to update stats and table
      await fetchReports();

      setSuccess(`Report ${status === 'dismissed' ? 'dismissed' : 'status updated'} successfully!`);
      setTimeout(() => setSuccess(''), 3000);

      // Close modal after successful update
      setTimeout(() => setShowModal(false), 1000);
    } catch (error) {
      console.error('Error updating report status:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update report status.';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  const openEditModal = (report) => {
    setEditingReport(report);
    setEditForm({ title: report.title || '', description: report.description || '' });
    setEditError('');
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editingReport) return;
    setEditLoading(true);
    setEditError('');
    try {
      await incidentReportsAPI.updateReport(editingReport.id, {
        title: editForm.title,
        description: editForm.description
      });
      // Update local state
      const updater = (r) => r.id === editingReport.id
        ? { ...r, title: editForm.title, description: editForm.description, isEdited: true }
        : r;
      setReports(prev => prev.map(updater));
      setFilteredReports(prev => prev.map(updater));
      if (selectedReport && selectedReport.id === editingReport.id) {
        setSelectedReport(prev => ({ ...prev, title: editForm.title, description: editForm.description, isEdited: true }));
      }
      setShowEditModal(false);
      setEditingReport(null);
      setSuccess('Report updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update report. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const variants = {
      low: 'success',
      medium: 'warning',
      high: 'orange',
      critical: 'danger'
    };
    return <Badge bg={variants[severity] || 'secondary'}>{severity?.toUpperCase()}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      submitted: 'primary',
      pending: 'primary',
      under_investigation: 'danger',
      reviewed: 'danger',
      resolved: 'success',
      dismissed: 'secondary'
    };
    const labels = {
      submitted: 'SUBMITTED',
      pending: 'SUBMITTED',
      under_investigation: 'UNDER INVESTIGATION',
      reviewed: 'UNDER INVESTIGATION',
      resolved: 'RESOLVED',
      dismissed: 'DISMISSED'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status?.toUpperCase()}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const exportReports = async () => {
    try {
      const response = await incidentReportsAPI.exportPDF(filters);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incident-reports-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Reports exported as PDF successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error exporting reports:', error);
      setError('Failed to export reports.');
    }
  };

  const handleDownloadReport = async () => {
    if (!selectedReport) return;
    
    setDownloadLoading(true);
    setError('');
    try {
      const response = await incidentReportsAPI.exportIndividualReportPDF(selectedReport.id);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = `incident-report-${selectedReport.id}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Report downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error downloading report:', error);
      setError('Failed to download report. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Container fluid className="incident-reports-container py-4">
      <Row className="mb-4">
        <Col>
          <h2>Incident Reports</h2>
          <p className="text-muted">Review and manage incident reports submitted by personnel</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Total Reports</h6>
              <h3>{stats.total}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Submitted</h6>
              <h3 className="text-primary">{stats.submitted}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Under Investigation</h6>
              <h3 className="text-danger">{stats.underInvestigation}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Resolved</h6>
              <h3 className="text-success">{stats.resolved}</h3>
            </Card.Body>
          </Card>
        </Col>

      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaSearch className="me-2" />
                  Search
                </Form.Label>
                <Form.Control
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search reports..."
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Severity</Form.Label>
                <Form.Select name="severity" value={filters.severity} onChange={handleFilterChange}>
                  <option value="">All</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                  <option value="">All</option>
                  <option value="submitted">Submitted</option>
                  <option value="under_investigation">Under Investigation</option>
                  <option value="resolved">Resolved</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
            <Col md={1} className="d-flex align-items-end">
              <Button variant="outline-secondary" onClick={resetFilters} className="mb-3 w-100">
                Reset
              </Button>
            </Col>
          </Row>
          <div className="d-flex justify-content-end">
            <Button variant="success" onClick={exportReports}>
              <FaFilePdf className="me-2" />
              Export as PDF
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Reports Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-3">Loading incident reports...</p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>No incident reports found.</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Reporter</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Location</th>
                  <th>Region</th>
                  <th>Branch</th>
                  <th>Date</th>
                  <th>Photos</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => {
                  // Handle different reporter field structures
                  const reporterName = report.reportedBy?.name ||
                    report.user?.name ||
                    report.User?.name ||
                    report.personnel?.name ||
                    report.Personnel?.name ||
                    'Unknown';
                  
                  // Handle region from different sources
                  const region = report.region || 
                                report.Site?.regionDetails?.name ||
                                report.Site?.Region?.region_name || 
                                report.Site?.Region?.name || '';
                  
                  // Handle branch from different sources
                  const branch = report.branch || 
                                report.Site?.branchDetails?.name ||
                                report.Site?.Branch?.branch_name || 
                                report.Site?.Branch?.name || '';
                  
                  return (
                    <tr key={report.id}>
                      <td>{report.title}</td>
                      <td>{reporterName}</td>
                      <td>{getSeverityBadge(report.severity)}</td>
                      <td>{getStatusBadge(report.status)}</td>
                      <td>{report.location || 'N/A'}</td>
                      <td>{region || 'N/A'}</td>
                      <td>{branch || 'N/A'}</td>
                      <td>{formatDate(report.createdAt)}</td>
                      <td>
                        <Badge bg="secondary">
                          {(report.photos?.length ||
                            report.photoPath?.length ||
                            (Array.isArray(report.photoPath) ? report.photoPath.length : 0) ||
                            0)}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => viewReport(report.id)}
                          >
                            <FaEye className="me-1" />
                            View
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => openEditModal(report)}
                          >
                            <FaEdit className="me-1" />
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Edit Report Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
        backdrop="static"
        dialogClassName="edit-report-modal"
      >
        <Modal.Header closeButton className="px-4 py-3 border-bottom">
          <Modal.Title className="fw-semibold" style={{ fontSize: '1.2rem' }}>
            <FaEdit className="me-2 text-secondary" style={{ fontSize: '1rem' }} />
            Edit Incident Report
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-4">
          {editError && (
            <Alert variant="danger" onClose={() => setEditError('')} dismissible>
              {editError}
            </Alert>
          )}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold mb-1">Title</Form.Label>
            <Form.Control
              type="text"
              value={editForm.title}
              onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
              disabled={editLoading}
              placeholder="Enter report title"
              style={{ fontSize: '1rem', padding: '0.6rem 0.85rem' }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold mb-1">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              value={editForm.description}
              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
              disabled={editLoading}
              placeholder="Enter report description"
              style={{ fontSize: '1rem', padding: '0.6rem 0.85rem', resize: 'vertical' }}
            />
          </Form.Group>
          <p className="text-muted small mb-0">
            Only <strong>Title</strong> and <strong>Description</strong> can be modified. All other fields are read-only.
          </p>
        </Modal.Body>
        <Modal.Footer className="px-4 py-3 border-top d-flex justify-content-end gap-2">
          <Button variant="outline-secondary" onClick={() => setShowEditModal(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSave}
            disabled={editLoading || !editForm.title.trim()}
          >
            {editLoading ? (
              <><Spinner as="span" animation="border" size="sm" className="me-1" />Saving...</>
            ) : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Report Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Incident Report Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReport && (
            <div>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Date:</strong> {formatDate(selectedReport.createdAt)}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Reporter:</strong> {selectedReport.reportedBy?.name ||
                    selectedReport.user?.name ||
                    selectedReport.User?.name ||
                    selectedReport.personnel?.name ||
                    selectedReport.Personnel?.name ||
                    'Unknown'}
                </Col>
                <Col md={6}>
                  <strong>Location:</strong> {selectedReport.location || 'N/A'}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Region:</strong> {
                    selectedReport.region || 
                    selectedReport.Site?.regionDetails?.name ||
                    selectedReport.Site?.Region?.region_name || 
                    selectedReport.Site?.Region?.name || 
                    'N/A'
                  }
                </Col>
                <Col md={6}>
                  <strong>Branch:</strong> {
                    selectedReport.branch || 
                    selectedReport.Site?.branchDetails?.name ||
                    selectedReport.Site?.Branch?.branch_name || 
                    selectedReport.Site?.Branch?.name || 
                    'N/A'
                  }
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Severity:</strong> {getSeverityBadge(selectedReport.severity)}
                </Col>
                <Col md={6}>
                  <strong>Status:</strong> {getStatusBadge(selectedReport.status)}
                </Col>
              </Row>

              <hr />

              <h5>
                Title
                {selectedReport.isEdited && (
                  <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>Edited</Badge>
                )}
              </h5>
              <p>{selectedReport.title}</p>

              <h5>Description</h5>
              <p>{selectedReport.description}</p>

              {(() => {
                // Handle multiple possible photo field names from different backend responses
                const photoArray = selectedReport.photos ||
                  selectedReport.photoPath ||
                  selectedReport.photoPaths ||
                  selectedReport.photoUrls ||
                  selectedReport.images ||
                  selectedReport.attachments ||
                  [];

                const photosToDisplay = Array.isArray(photoArray) ? photoArray :
                  (photoArray ? [photoArray] : []);

                console.log('=== PHOTO DEBUG ===');
                console.log('Selected Report:', selectedReport);
                console.log('Photo Array:', photoArray);
                console.log('Photos to Display:', photosToDisplay);
                console.log('Photo Count:', selectedReport.photoCount);

                if (photosToDisplay.length === 0) {
                  return (
                    <>
                      <h5>Photo Evidence</h5>
                      <p className="text-muted">No photos attached to this report.</p>
                      <p className="text-warning small">Debug: Expected {selectedReport.photoCount || 0} photos but none found in response data.</p>
                    </>
                  );
                }

                return (
                  <>
                    <h5>Photo Evidence ({photosToDisplay.length})</h5>
                    <Row>
                      {photosToDisplay.map((photo, index) => {
                        // Handle both string paths and object formats
                        let photoUrl = '';

                        if (typeof photo === 'string') {
                          // If it's a string path, check if it already includes /uploads/
                          photoUrl = photo.startsWith('http')
                            ? photo
                            : photo.startsWith('/')
                              ? `${API_BASE_URL}${photo}`
                              : `${API_BASE_URL}/${photo}`;
                        } else if (typeof photo === 'object') {
                          // If it's an object, try multiple possible fields
                          const photoPath = photo.url || photo.path || photo.photoPath || photo.src;
                          photoUrl = photoPath?.startsWith('http')
                            ? photoPath
                            : photoPath?.startsWith('/')
                              ? `${API_BASE_URL}${photoPath}`
                              : `${API_BASE_URL}/${photoPath}`;
                        }

                        console.log(`Photo ${index + 1} Raw:`, photo);
                        console.log(`Photo ${index + 1} URL:`, photoUrl);

                        return (
                          <Col md={4} key={index} className="mb-3">
                            <img
                              src={photoUrl}
                              alt={`Evidence ${index + 1}`}
                              className="img-fluid rounded border"
                              style={{ cursor: 'pointer', maxHeight: '300px', objectFit: 'cover', width: '100%' }}
                              onClick={() => window.open(photoUrl, '_blank')}
                              onError={(e) => {
                                console.error(`Failed to load photo ${index + 1}:`, photoUrl);
                                e.target.onerror = null; // Prevent infinite loop
                                e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                              }}
                            />
                            <div className="mt-2 small text-muted">
                              Photo {index + 1}
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </>
                );
              })()}

              {/* Document Attachments */}
              {(() => {
                const documentsArray = selectedReport.documents || [];
                
                if (documentsArray.length === 0) {
                  return null; // Don't show section if no documents
                }

                return (
                  <>
                    <hr />
                    <h5>Document Attachments ({documentsArray.length})</h5>
                    <div className="list-group">
                      {documentsArray.map((doc, index) => {
                        // Handle both string paths and object formats
                        const docPath = typeof doc === 'string' ? doc : (doc.path || doc.url);
                        const docName = typeof doc === 'object' ? (doc.name || docPath?.split('/').pop()) : docPath?.split('/').pop();
                        const docUrl = docPath?.startsWith('http') 
                          ? docPath 
                          : docPath?.startsWith('/') 
                            ? `${API_BASE_URL}${docPath}` 
                            : `${API_BASE_URL}/${docPath}`;

                        return (
                          <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                            <span>
                              <FaFileAlt className="me-2" />
                              {docName || `Document ${index + 1}`}
                            </span>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => window.open(docUrl, '_blank')}
                            >
                              <FaDownload className="me-1" />
                              Download
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}

              {/* Comments Section */}
              <hr />
              <h5><FaComment className="me-2" />Admin Comments</h5>

              {/* Existing comments */}
              {comments.length === 0 ? (
                <p className="text-muted small">No comments yet. Be the first to leave feedback.</p>
              ) : (
                <div className="mb-3" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                  {comments.map((c, idx) => (
                    <div key={c.id || idx} className="border rounded p-2 mb-2 bg-light">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong className="small">{c.author?.name || c.adminName || 'Admin'}</strong>
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}
                        </span>
                      </div>
                      <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{c.body || c.text || c.comment}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* New comment input */}
              {commentError && (
                <Alert variant="danger" className="py-1 px-2 small mb-2" onClose={() => setCommentError('')} dismissible>
                  {commentError}
                </Alert>
              )}
              <Form.Group className="mb-2">
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Write your feedback or comment here..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  disabled={commentLoading}
                />
              </Form.Group>
              <Button
                variant="primary"
                size="sm"
                onClick={submitComment}
                disabled={commentLoading || !commentText.trim()}
              >
                {commentLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                    Posting...
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="me-1" />
                    Post Comment
                  </>
                )}
              </Button>

              <hr />

              <h5>Update Status</h5>
              <div className="d-flex gap-2">
                <Button
                  variant="danger"
                  onClick={() => updateReportStatus(selectedReport.id, 'under_investigation')}
                  disabled={selectedReport.status === 'under_investigation' || selectedReport.status === 'resolved'}
                >
                  Under Investigation
                </Button>
                <Button
                  variant="success"
                  onClick={() => updateReportStatus(selectedReport.id, 'resolved')}
                  disabled={selectedReport.status === 'resolved'}
                >
                  Resolved
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={handleDownloadReport}
            disabled={downloadLoading}
          >
            {downloadLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Downloading...
              </>
            ) : (
              <>
                <FaDownload className="me-2" />
                Download Report
              </>
            )}
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default IncidentReports;
